'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { useParams } from 'next/navigation';
import { shallow } from 'zustand/shallow';

import { Card, CardContent } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Badge } from '@/components/ui/badge';
import { Play } from 'lucide-react';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import DynamicImage from '@/components/commons/dynamic-image';
import ModalImagePreview from '@/components/sections/builder/elements/body/product-detail/components/modal-image-preview';
import FixedAddToCart from '@/components/sections/builder/elements/body/product-detail/fixed-add-to-cart';

import { useBuilderStore } from '@/stores/builder';
import { useProductStore } from '@/stores/product';
import { useCartProduct } from '@/stores/cart';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn, extractYouTubeVideoId, isEmpty } from '@/lib/utils';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';

import { ElementProps } from '@/components/sections/builder/types';
import { ElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { ICardVariants, IProduct, ProductAssets, ProductItem } from '@/types/product';

/**
 * Utilities (pure, reusable)
 */
function setIfChanged<T>(setter: (v: T) => void, next: T, curr: T) {
  if (next !== curr) setter(next);
}

function getUniqueImages(assets: ProductAssets[] = []) {
  const seen = new Set();
  return assets.filter(
    (asset) =>
      asset.assets_type === 'image' &&
      !seen.has(asset.assets_url) &&
      (seen.add(asset.assets_url), true)
  );
}

function useIsoLayoutEffect(effect: React.EffectCallback, deps: React.DependencyList) {
  const isClient = typeof window !== 'undefined';
  return (isClient ? useLayoutEffect : useEffect)(effect, deps);
}

function useNextFrame(effect: () => void, deps: React.DependencyList) {
  useEffect(() => {
    const id = requestAnimationFrame(() => effect());
    return () => cancelAnimationFrame(id);
  }, deps);
}

export default function ElementContent({ element, preview, theme, data }: ElementProps) {
  const { slug: productSlug } = useParams<{ slug: string }>();
  const customerToken = Cookies.get('accessToken') as string;
  const isMobile = useMediaQuery(768);

  const [mainImage, setMainImage] = useState<string>('');
  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [mainIframe, setMainIframe] = useState<string | null>(null);
  const [isLoadedMedia, setIsLoadedMedia] = useState<boolean>(false);

  const initedRef = useRef<string | null>(null);

  const builder = useBuilderStore((s) => ({ theme: s.theme }), shallow);

  const cartProduct = useCartProduct(
    (s) => ({
      clear: s.clear,
      quantity: s.quantity,
      sell_price: s.sell_price,
      discount_price: s.discount_price,
      setQuantity: s.setQuantity,
    }),
    shallow
  );

  const productStore = useProductStore(
    (s) => ({
      quantity: s.quantity,
      price: s.price,
      discountPrice: s.discountPrice,
      reset: s.reset,
      imageVariant: s.imageVariant,
      allSelectedVariants: s.allSelectedVariants,
      isApplicablePricebook: s.isApplicablePricebook,
      setPrice: s.setPrice,
      setQuantity: s.setQuantity,
      setDiscountPrice: s.setDiscountPrice,
      setSelectedOptions: s.setSelectedOptions,
      setAllSelectedVariants: s.setAllSelectedVariants,
      setImageVariant: s.setImageVariant,
    }),
    shallow
  );

  const themeContent = preview ? builder.theme : theme;
  const content = element.data as ElementDataType;
  const childs = element.childs;

  const product: IProduct = (data as IProduct) || ({} as IProduct);

  const productKey = useMemo(
    () => product?.slug || product?.product_group_id || '',
    [product?.slug, product?.product_group_id]
  );

  const productItems: ProductItem[] = useMemo(
    () => product?.product_items || [],
    [product?.product_items]
  );
  const productVariants: ICardVariants[] = useMemo(
    () => product?.variants || [],
    [product?.variants]
  );

  const availableQty = useMemo(
    () => productItems.reduce((acc, item) => acc + Math.max(Number(item.stock || 0), 0), 0),
    [productItems]
  );

  const firstItem = productItems?.[0];
  const hasVariant = (productVariants?.length || 0) > 0;

  const quantity = productStore.quantity;
  const selectedVariant = productStore.allSelectedVariants;
  const isSoldOut = selectedVariant ? quantity <= 0 : availableQty <= 0;

  const images = useMemo(() => getUniqueImages(product.product_assets), [product?.product_assets]);
  const imagesVariants = useMemo(
    () => getUniqueImages(productStore.imageVariant),
    [productStore.imageVariant]
  );
  const videoId = useMemo(
    () => (product.youtube_link ? extractYouTubeVideoId(product.youtube_link) : null),
    [product.youtube_link]
  );

  const discountPercentage = useMemo(() => {
    const calc = (sell?: number | null, disc?: number | null) =>
      sell && disc ? ((sell - disc) / sell) * 100 : 0;
    if ((productVariants?.length || 0) > 1) {
      return calc(cartProduct?.sell_price, cartProduct?.discount_price);
    }
    return calc(firstItem?.sell_price, firstItem?.discount_price);
  }, [
    productVariants.length,
    firstItem?.sell_price,
    firstItem?.discount_price,
    cartProduct.sell_price,
    cartProduct.discount_price,
  ]);

  const mediaItems = useMemo(() => {
    const media = [...images];
    if (product.youtube_link) {
      const videoItem = {
        assets_type: 'video',
        assets_url: product.youtube_link,
      };
      media.splice(1, 0, videoItem);
    }
    return media;
  }, [images, product.youtube_link]);

  const desiredSnapshot = useMemo(() => {
    const price = Number(firstItem?.sell_price ?? product?.sell_price ?? 0);
    const discount = Number(firstItem?.discount_price ?? product?.discount_price ?? 0);
    const qty = Number(firstItem?.stock ?? product?.available_qty ?? 0);
    return { price, discount, qty };
  }, [
    productKey,
    firstItem?.sell_price,
    firstItem?.discount_price,
    firstItem?.stock,
    product?.sell_price,
    product?.discount_price,
    product?.available_qty,
  ]);

  const dataReady = useMemo(() => {
    const fiReady =
      !!firstItem &&
      (firstItem.sell_price != null || firstItem.discount_price != null || firstItem.stock != null);
    const rootReady =
      product?.sell_price != null ||
      product?.discount_price != null ||
      product?.available_qty != null;
    return fiReady || rootReady;
  }, [
    firstItem?.sell_price,
    firstItem?.discount_price,
    firstItem?.stock,
    product?.sell_price,
    product?.discount_price,
    product?.available_qty,
  ]);

  const syncNoVariant = useCallback(() => {
    if (!productKey || hasVariant || !dataReady) return;

    const { price, discount, qty } = desiredSnapshot;

    setIfChanged(productStore.setPrice, price, productStore.price);
    setIfChanged(productStore.setDiscountPrice, discount, productStore.discountPrice);
    setIfChanged(productStore.setQuantity, qty, productStore.quantity);
    setIfChanged(
      cartProduct.setQuantity,
      hasVariant && selectedVariant ? (quantity > 0 ? 1 : 0) : availableQty > 0 ? 1 : 0,
      cartProduct.quantity
    );

    // reset pilihan SEKALI per produk
    if (initedRef.current !== productKey) {
      productStore.setSelectedOptions({});
      productStore.setAllSelectedVariants(false);
      productStore.setImageVariant([]);
      initedRef.current = productKey;
    }
  }, [
    productKey,
    hasVariant,
    dataReady,
    desiredSnapshot.price,
    desiredSnapshot.discount,
    desiredSnapshot.qty,
    productStore.price,
    productStore.discountPrice,
    productStore.quantity,
  ]);

  const handleMediaClick = (url: string, type: 'image' | 'iframe') => {
    if (type === 'image') {
      setMainIframe(null);
      if (url !== mainImage) setMainImage(url);
    } else {
      setMainImage('');
      setMainIframe(`https://www.youtube.com/embed/${videoId}?autoplay=1`);
    }
  };

  const pickMainImage = useCallback(() => {
    const fromVariant = imagesVariants?.[0]?.assets_url;
    const fromImages = images?.[0]?.assets_url;
    return (productVariants?.length ? fromVariant : undefined) || fromVariant || fromImages || '';
  }, [productVariants?.length, imagesVariants, images]);

  useIsoLayoutEffect(() => {
    setOpenPreview(false);
    setMainIframe(null);

    const candidate = pickMainImage();
    setIfChanged(setMainImage, candidate, mainImage);
  }, [productKey]);

  useEffect(() => {
    const candidate = pickMainImage();
    if (candidate && candidate !== mainImage) {
      setMainImage(candidate);
    }
  }, [images, imagesVariants]);

  useIsoLayoutEffect(() => {
    syncNoVariant();
  }, [syncNoVariant]);

  useNextFrame(() => {
    syncNoVariant();
  }, [syncNoVariant]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rerun = () => syncNoVariant();
    window.addEventListener('pageshow', rerun as any);
    window.addEventListener('popstate', rerun);
    window.addEventListener('focus', rerun);
    return () => {
      window.removeEventListener('pageshow', rerun as any);
      window.removeEventListener('popstate', rerun);
      window.removeEventListener('focus', rerun);
    };
  }, [syncNoVariant]);

  useEffect(() => {
    if (!productKey || !hasVariant) return;
    if (initedRef.current !== productKey) {
      productStore.setSelectedOptions({});
      productStore.setAllSelectedVariants(false);
      productStore.setImageVariant([]);
      initedRef.current = productKey;
    }
  }, [productKey, hasVariant]);

  useEffect(() => {
    setIsLoadedMedia(false);
  }, [mainImage, mainIframe]);

  const viewKey = `${preview ? 'preview' : 'live'}:${productKey}`;
  useEffect(() => {
    if (preview || !product?.product_group_id) return;

    SendEventAnalytic('view_item', {
      items: [
        {
          item_id: product.product_group_id,
          item_name: product.product_name,
          item_category: product.category_name,
          item_variant: product.variants,
          price: product.sell_price,
          quantity: product.available_qty,
        },
      ],
    });

    SendEventMetapixel('ViewContent', {
      content_ids: [product.product_group_id],
      content_name: product.product_name,
      content_type: 'product',
      contents: productItems
        ?.filter((item) => !!item.item_id)
        .map((item) => ({
          id: item.item_id,
          quantity: item.stock,
        })),
      value: product.sell_price,
      currency: 'IDR',
    });
  }, [viewKey]);

  const BADGES_PRODUCT = [
    product?.best_seller && {
      text: themeContent?.badge?.bestSellerText || 'Best Seller',
      className:
        themeContent?.badge?.position === 'inside'
          ? 'theme_badge_best_seller_gradient'
          : 'theme_badge_best_seller',
    },
    product?.new_arrival && {
      text: themeContent?.badge?.newArrivalText || 'New Arrival',
      className:
        themeContent?.badge?.position === 'inside'
          ? 'theme_badge_new_arrival_gradient'
          : 'theme_badge_new_arrival',
    },
    discountPercentage > 0 &&
      productStore.isApplicablePricebook === false && {
        text: `${themeContent?.badge?.promotionText || 'Discount'} ${discountPercentage.toFixed(
          0
        )}%`,
        className:
          themeContent?.badge?.position === 'inside'
            ? 'theme_badge_promotion_gradient'
            : 'theme_badge_promotion',
      },
  ].filter(Boolean) as { text: string; className: string }[];

  return (
    <>
      <section
        className={cn('bg-white', !content.isShowSecondaryBackground && 'bg_product_detail')}
      >
        <div className='theme_layout_page_width product_detail_wrapper mx-auto duration-100'>
          <div className='container mx-auto max-w-screen-xl px-4 lg:px-16'>
            <div
              className={cn(
                'flex flex-col ',
                (content.galleryPosition || 'horizontal') !== 'vertical' &&
                  'gap-4 md:gap-16 lg:gap-16',
                content.layoutPosition === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'
              )}
            >
              <div className='w-full space-y-3 md:w-1/2'>
                <div
                  className={cn(content.enableStickyProductDetail && 'top-sticky-image md:sticky')}
                >
                  <Card
                    className='cursor-pointer overflow-hidden border-none shadow-none'
                    style={{ borderRadius: `${builder.theme.media?.borderCornerRadius || 0}px` }}
                  >
                    <CardContent className='flex items-center justify-center p-0'>
                      {mainImage !== '' ? (
                        <DynamicImage
                          key={mainImage}
                          onClick={() => setOpenPreview(true)}
                          src={mainImage}
                          alt={product.product_name || 'Product image'}
                          width={600}
                          height={600}
                          sizes='(max-width: 768px) 100vw, 600px'
                          className={cn(
                            'main-media aspect-square w-full object-contain transition-all duration-200',
                            isLoadedMedia ? 'opacity-100' : 'opacity-0'
                          )}
                          onLoad={() => setIsLoadedMedia(true)}
                        />
                      ) : mainIframe ? (
                        <iframe
                          width='600'
                          height='600'
                          src={mainIframe}
                          allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
                          className={cn(
                            'main-media aspect-square w-full object-contain transition-all duration-200',
                            isLoadedMedia ? 'opacity-100' : 'opacity-0'
                          )}
                          onLoad={() => setIsLoadedMedia(true)}
                        ></iframe>
                      ) : null}
                    </CardContent>
                  </Card>
                  {/* carousel horizontal */}
                  {(isMobile || (content.galleryPosition || 'horizontal') === 'horizontal') && (
                    <Carousel
                      opts={{
                        align: 'start',
                        loop: true,
                      }}
                      className='mt-4 w-full'
                    >
                      <CarouselContent>
                        {mediaItems?.map((item, index) => (
                          <CarouselItem key={index} className='basis-1/4'>
                            <div className='p-1'>
                              {item.assets_type === 'image' ? (
                                <DynamicImage
                                  src={item.assets_url}
                                  alt={`Media ${index + 1}`}
                                  width={100}
                                  height={100}
                                  className='aspect-square h-auto w-full cursor-pointer rounded border-2 object-cover'
                                  onClick={() => handleMediaClick(item.assets_url, 'image')}
                                  style={
                                    !isEmpty(content.colorSchemeProductDetail)
                                      ? {
                                          borderColor:
                                            item.assets_url === mainImage
                                              ? content.colorSchemeProductDetail?.textSecondary
                                              : 'transparent',
                                        }
                                      : {}
                                  }
                                />
                              ) : (
                                <div
                                  className='relative aspect-square w-full cursor-pointer rounded border-2 bg-white'
                                  onClick={() => handleMediaClick(item.assets_url, 'iframe')}
                                >
                                  <DynamicImage
                                    src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                                    alt={`Thumbnail video ${index + 1}`}
                                    width={320}
                                    height={180}
                                    className='h-full w-full rounded bg-white object-contain'
                                  />
                                  <div className='absolute inset-0 flex items-center justify-center'>
                                    <div className='rounded-full bg-slate-200 p-1'>
                                      <Play className='h-6 w-6 text-slate-100' fill='true' />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      {mediaItems.length > 4 && !isMobile && (
                        <>
                          <CarouselPrevious />
                          <CarouselNext />
                        </>
                      )}
                    </Carousel>
                  )}
                </div>
              </div>
              {/* carousel  vertical*/}
              {content.galleryPosition === 'vertical' && (
                <div className='hidden items-center overflow-hidden  md:flex md:w-[100px] md:flex-col lg:w-[121px]'>
                  <Carousel
                    opts={{
                      align: 'start',
                      loop: true,
                    }}
                    className='w-3/4 items-center justify-center '
                    orientation='vertical'
                  >
                    <CarouselContent className='mt-1  md:h-[20rem] lg:h-[27rem]'>
                      {mediaItems?.map((item, index) => (
                        <CarouselItem key={index} className='basis-1/4'>
                          <div className='p-1'>
                            {item.assets_type === 'image' ? (
                              <DynamicImage
                                src={item.assets_url}
                                alt={`Media ${index + 1}`}
                                width={100}
                                height={100}
                                className='aspect-square h-auto w-full cursor-pointer rounded border-2 object-cover'
                                onClick={() => handleMediaClick(item.assets_url, 'image')}
                                style={
                                  !isEmpty(content.colorSchemeProductDetail)
                                    ? {
                                        borderColor:
                                          item.assets_url === mainImage
                                            ? content.colorSchemeProductDetail?.textSecondary
                                            : 'transparent',
                                      }
                                    : {}
                                }
                              />
                            ) : (
                              <div
                                className='relative aspect-square w-full cursor-pointer rounded border-2 bg-white'
                                onClick={() => handleMediaClick(item.assets_url, 'iframe')}
                              >
                                <DynamicImage
                                  src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                                  alt={`Thumbnail video ${index + 1}`}
                                  width={320}
                                  height={180}
                                  className='h-full w-full rounded bg-white object-contain'
                                />
                                <div className='absolute inset-0 flex items-center justify-center'>
                                  <div className='rounded-full bg-slate-200 p-1'>
                                    <Play className='h-6 w-6 text-slate-100' fill='true' />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {mediaItems.length > 4 && (
                      <>
                        <CarouselPrevious className='left-1/2 top-0 -translate-x-1/2 rotate-90' />
                        <CarouselNext className='-bottom-4 left-1/2 z-10 -translate-x-1/2 rotate-90' />
                      </>
                    )}
                  </Carousel>
                </div>
              )}
              <div className='w-full space-y-4 md:w-1/2'>
                {isSoldOut ? (
                  <div className='w-full'>
                    <Badge
                      variant='custom'
                      className='theme_badge_inside w-full bg-[#616161] text-xs font-bold text-white'
                    >
                      {themeContent?.badge?.soldOutText || 'Sold Out'}
                    </Badge>
                  </div>
                ) : (
                  BADGES_PRODUCT.length > 0 && (
                    <div
                      className={cn(
                        'grid w-full gap-2',
                        BADGES_PRODUCT.length === 1 ? 'grid-cols-1' : '',
                        BADGES_PRODUCT.length === 2 ? 'grid-cols-2' : '',
                        BADGES_PRODUCT.length === 3 ? 'grid-cols-2 grid-rows-2' : ''
                      )}
                    >
                      {BADGES_PRODUCT.map((badge, index) => (
                        <Badge
                          key={index}
                          variant='custom'
                          className={cn(
                            'theme_badge_inside w-full text-xs font-bold',
                            badge.className,
                            BADGES_PRODUCT.length === 3 && index === 2 ? 'col-span-2' : ''
                          )}
                        >
                          {badge.text}
                        </Badge>
                      ))}
                    </div>
                  )
                )}
                {(childs || []).map((child) => (
                  <WrapperChild
                    child={{
                      ...child,
                      parentData: {
                        ...product,
                        ...content,
                        slug: productSlug,
                        token: customerToken,
                        theme: themeContent,
                      },
                      theme,
                    }}
                    parentType={element.type}
                    key={child.id}
                    preview={preview}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {themeContent?.productCard?.positionButtonAddToCart === 'fixed' && (
        <div className={cn('z-50 bg-white shadow-lg transition-transform duration-300')}>
          <div
            id='fixed-add-to-cart'
            className='fixed bottom-0 left-0 right-0 z-[999] rounded-t-3xl border border-b-0 bg-white px-4 py-4 md:mx-20 lg:mx-20 lg:px-10 xl:mx-20 xl:rounded-t-xl'
          >
            <FixedAddToCart product={product} theme={theme} preview={preview} />
          </div>
        </div>
      )}

      {openPreview && (
        <ModalImagePreview
          images={[
            mainImage,
            ...images
              .filter((image) => image.assets_url !== mainImage)
              .map((image) => image.assets_url),
          ].filter(Boolean)}
          initialIndex={[
            mainImage,
            ...images.filter((image) => image.assets_url !== mainImage),
          ].findIndex((img) => img === mainImage)}
          isOpen={openPreview}
          onClose={() => setOpenPreview(false)}
          content={content as ElementDataType}
        />
      )}
      <style jsx>
        {`
          .product_detail_wrapper {
            padding-top: ${content.topPadding}px;
            padding-bottom: ${content.bottomPadding}px;
          }
          .bg_product_detail {
            background-color: ${content.colorSchemeProductDetail?.background || 'white'};
            color: ${content.colorSchemeProductDetail?.text || '#52535B'} !important;
          }
          @media (min-width: 768px) {
            .top-sticky-image {
              top: ${content.topImage || 0}px;
            }
          }
          .main-media {
            will-change: opacity, transform;
          }
        `}
      </style>
    </>
  );
}
